import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Upload, 
  Play, 
  Eye, 
  EyeOff,
  BookOpen,
  X,
  Save
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import LoadingSpinner, { ErrorMessage } from './LoadingSpinner';
import { useToast } from '../hooks/use-toast';

const TutorialManagement = ({ isOpen, onClose, adminToken }) => {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState(null);
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    order: 0,
    embed_url: ''
  });

  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    order: 0,
    is_active: true,
    embed_url: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadTutorials();
    }
  }, [isOpen]);

  const loadTutorials = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tutorials`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Sort by order number (ascending) - convert to integer to handle string/number mix
        const sortedData = [...data].sort((a, b) => {
          const orderA = parseInt(a.order, 10) || 0;
          const orderB = parseInt(b.order, 10) || 0;
          return orderA - orderB;
        });
        setTutorials(sortedData);
      } else {
        setError('Failed to load tutorials');
      }
    } catch (err) {
      console.error('Error loading tutorials:', err);
      setError('Failed to load tutorials');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTutorial = async (e) => {
    e.preventDefault();
    
    if (!formData.embed_url) {
      toast({
        title: "Error",
        description: "Please enter a YouTube embed URL",
        variant: "destructive"
      });
      return;
    }

    try {
      setCreating(true);
      
      const createForm = new FormData();
      createForm.append('title', formData.title);
      createForm.append('description', formData.description);
      createForm.append('embed_url', formData.embed_url);
      createForm.append('category', formData.category);
      createForm.append('order', formData.order.toString());
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tutorials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        },
        body: createForm
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Tutorial created successfully"
        });
        setShowAddForm(false);
        setFormData({
          title: '',
          description: '',
          category: 'general',
          order: 0,
          embed_url: ''
        });
        loadTutorials();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create tutorial');
      }
    } catch (err) {
      console.error('Error creating tutorial:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to create tutorial",
        variant: "destructive"
      });
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateTutorial = async (tutorialId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tutorials/${tutorialId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editFormData)
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Tutorial updated successfully"
        });
        setEditingTutorial(null);
        loadTutorials();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update tutorial');
      }
    } catch (err) {
      console.error('Error updating tutorial:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to update tutorial",
        variant: "destructive"
      });
    }
  };

  const handleDeleteTutorial = async (tutorialId) => {
    if (!window.confirm('Are you sure you want to delete this tutorial? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tutorials/${tutorialId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Tutorial deleted successfully"
        });
        loadTutorials();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete tutorial');
      }
    } catch (err) {
      console.error('Error deleting tutorial:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to delete tutorial",
        variant: "destructive"
      });
    }
  };

  const startEditing = (tutorial) => {
    setEditingTutorial(tutorial.id);
    setEditFormData({
      title: tutorial.title,
      description: tutorial.description,
      category: tutorial.category,
      order: tutorial.order,
      is_active: tutorial.is_active,
      embed_url: tutorial.embed_url || ''
    });
  };

  if (!isOpen) return null;

  const handleFixOrder = async () => {
    if (!window.confirm('This will renumber all tutorials sequentially (1, 2, 3, ...). Continue?')) {
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tutorials/fix-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Success",
          description: result.message
        });
        loadTutorials();
      } else {
        throw new Error('Failed to fix order');
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to fix tutorial order",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold flex items-center">
              <BookOpen className="h-6 w-6 mr-3" />
              Tutorial Management
            </h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleFixOrder} className="text-orange-600 border-orange-600 hover:bg-orange-50">
                Fix Numbering
              </Button>
              <Button onClick={() => setShowAddForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Tutorial
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <ErrorMessage message={error} onRetry={loadTutorials} />
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {tutorials.map(tutorial => (
                <Card key={tutorial.id} className="flex flex-col group hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    {/* YouTube Video Embed */}
                    <div className="relative bg-black rounded overflow-hidden mb-3">
                      {tutorial.embed_url ? (
                        <iframe
                          className="w-full aspect-video"
                          src={tutorial.embed_url}
                          title={tutorial.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      ) : (
                        <div className="w-full aspect-video flex items-center justify-center bg-gray-200">
                          <Play className="h-12 w-12 text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    {/* Title and Info */}
                    {editingTutorial === tutorial.id ? (
                      <div className="space-y-2">
                        <Input
                          value={editFormData.title}
                          onChange={(e) => setEditFormData({...editFormData, title: e.target.value})}
                          placeholder="Title"
                          className="text-sm"
                        />
                        <Textarea
                          value={editFormData.description}
                          onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                          placeholder="Description"
                          rows={2}
                          className="text-xs"
                        />
                        <Input
                          value={editFormData.embed_url}
                          onChange={(e) => setEditFormData({...editFormData, embed_url: e.target.value})}
                          placeholder="YouTube Embed URL"
                          className="text-xs"
                        />
                        <div className="flex gap-2 items-center">
                          <Input
                            value={editFormData.category}
                            onChange={(e) => setEditFormData({...editFormData, category: e.target.value})}
                            placeholder="Category"
                            className="flex-1 text-xs"
                          />
                          <div className="flex items-center gap-1">
                            <Label className="text-xs text-gray-500 whitespace-nowrap">Tutorial #</Label>
                            <Input
                              type="number"
                              value={editFormData.order}
                              onChange={(e) => setEditFormData({...editFormData, order: parseInt(e.target.value) || 0})}
                              className="w-20 text-xs text-center"
                              min="0"
                              max="999"
                            />
                          </div>
                        </div>
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={editFormData.is_active}
                            onChange={(e) => setEditFormData({...editFormData, is_active: e.target.checked})}
                          />
                          Active
                        </label>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            onClick={() => handleUpdateTutorial(tutorial.id)}
                            className="flex-1 h-7 text-xs"
                          >
                            <Save className="h-3 w-3 mr-1" />
                            Save
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => setEditingTutorial(null)}
                            className="h-7 w-7 p-0"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-semibold text-sm line-clamp-2 flex-1">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-700 text-xs font-bold mr-2">
                              {tutorial.order}
                            </span>
                            {tutorial.title}
                          </h3>
                          <Badge variant={tutorial.is_active ? 'default' : 'secondary'} className="text-xs flex-shrink-0">
                            {tutorial.is_active ? '✓' : '✗'}
                          </Badge>
                        </div>
                        
                        <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                          {tutorial.description}
                        </p>
                        
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                          <Badge variant="outline" className="text-xs">
                            {tutorial.category}
                          </Badge>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => startEditing(tutorial)}
                            className="flex-1 h-7 text-xs"
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleDeleteTutorial(tutorial.id)}
                            className="h-7 w-7 p-0"
                          >
                            <Trash2 className="h-3 w-3 text-red-600" />
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}

              {tutorials.length === 0 && (
                <div className="col-span-2 text-center py-8 text-gray-500">
                  <BookOpen className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">No Tutorials Yet</h3>
                  <p>Create your first tutorial to help users learn the application</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Tutorial Form Modal */}
        {showAddForm && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Add New Tutorial</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateTutorial} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows={3}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="embed_url">YouTube Embed URL or Full YouTube URL</Label>
                    <Input
                      id="embed_url"
                      value={formData.embed_url}
                      onChange={(e) => setFormData({...formData, embed_url: e.target.value})}
                      placeholder="https://www.youtube.com/embed/VIDEO_ID or https://www.youtube.com/watch?v=VIDEO_ID"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Paste any YouTube URL (watch, embed, or youtu.be link)
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <Input
                        id="category"
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        placeholder="e.g., general, setup, features"
                      />
                    </div>
                    <div>
                      <Label htmlFor="order">Tutorial #</Label>
                      <Input
                        id="order"
                        type="number"
                        value={formData.order}
                        onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                        min="0"
                        max="999"
                        placeholder="18"
                        className="text-center"
                      />
                      <p className="text-xs text-gray-500 mt-1">Enter the tutorial number (e.g., 18)</p>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button type="submit" disabled={creating} className="flex-1">
                      {creating ? (
                        <>
                          <LoadingSpinner className="h-4 w-4 mr-2" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Create Tutorial
                        </>
                      )}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowAddForm(false)}
                      disabled={creating}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default TutorialManagement;